import React from "react";
import Select from "react-select";
import {CohortRule, Option, phenotypicDataOptions} from "../../types/formTypes";
import {SelectedView} from "../../types/types";

interface CohortRulesSectionProps {
    label: string;
    rules: CohortRule[];
    setRules: React.Dispatch<React.SetStateAction<CohortRule[]>>;
    treeData: Record<SelectedView, any[]>;
    error?: boolean;
    required?: boolean;
}

const CohortRulesSection: React.FC<CohortRulesSectionProps> = ({
    label,
    rules,
    setRules,
    treeData,
    error,
    required = true
}) => {
    const handleTypeChange = (index: number, option: Option | null) => {
        const updated = [...rules];
        updated[index].type = option ? option.value : "";
        updated[index].value = ""; // Reset value when type changes
        setRules(updated);
    };

    const handleValueChange = (index: number, option: Option | null) => {
        const updated = [...rules];
        updated[index].value = option ? option.value : "";
        setRules(updated);
    };

    const addRule = () => {
        setRules([...rules, {type: "", value: ""}]);
    };

    const removeRule = (index: number) => {
        const updated = rules.filter((_, i) => i !== index);
        setRules(updated);
    };

    const extractLeafNodes = (nodes: any[]): Option[] => {
        const leafNodes: Option[] = [];

        const traverse = (node: any) => {
            if (!node.children || node.children.length === 0) {
                leafNodes.push({
                    value: node.value || node.key,
                    label: node.title
                });
            } else {
                node.children.forEach(traverse);
            }
        };

        nodes.forEach(traverse);
        return leafNodes;
    };

    return (
        <div className="form-field">
            <label className={required ? "required-field" : ""}>{label}</label>
            <div className="sub-label">Events having any of the following criteria:</div>
            {rules.map((rule, index) => {
                const leafNodeOptions = rule.type
                    ? extractLeafNodes(treeData[rule.type as SelectedView] || [])
                    : [];

                const isTypeSelected = !!rule.type;
                const isValueMissing = isTypeSelected && !rule.value;

                return (
                    <div className="cohort-rule-row" key={`${label}-${index}`}>
                        <div className="cohort-rule-select">
                            <Select
                                options={phenotypicDataOptions}
                                value={phenotypicDataOptions.find(opt => opt.value === rule.type) || null}
                                onChange={(option) => handleTypeChange(index, option)}
                                placeholder="Select rule type"
                                className={`react-select-container ${error ? 'error-border' : ''}`}
                                isClearable={true}
                            />
                        </div>
                        <div className="cohort-rule-tree">
                            <Select
                                options={leafNodeOptions}
                                onChange={(option) => handleValueChange(index, option)}
                                placeholder="Select criteria"
                                className={`react-select-container ${(error && isValueMissing) ? 'error-border' : ''}`}
                                isDisabled={!rule.type}
                                isClearable={true}
                                value={
                                    leafNodeOptions.find(option => option.value === rule.value) || null
                                }
                            />
                        </div>
                        <div className="cohort-rule-actions">
                            {rules.length > 1 && (
                                <button className="remove-btn" type="button"
                                        onClick={() => removeRule(index)}>-</button>
                            )}
                            {index === rules.length - 1 && (
                                <button className="add-btn" type="button" onClick={addRule}>+</button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default CohortRulesSection;